import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Company, CompanyAlias } from "@ceo-cmd/shared";
import {
  archiveOrgCompany,
  createOrgCompany,
  getOrgPortfolio,
  getOrgWorkspace,
  setOrgCompanyAliases,
  updateOrgCompany,
} from "@/api/org";
import {
  SettingsPageHeader,
  SettingsSection,
} from "@/components/settings/settings-ui";

function formatAliases(aliases: CompanyAlias[] | undefined): string {
  if (!aliases?.length) return "None";
  return aliases.map((a) => `${a.system}:${a.externalId}`).join(", ");
}

function parseAliasLines(text: string): CompanyAlias[] {
  return text
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const colon = line.indexOf(":");
      if (colon <= 0) return null;
      return {
        system: line.slice(0, colon).trim().toLowerCase(),
        externalId: line.slice(colon + 1).trim(),
      };
    })
    .filter((a): a is CompanyAlias => Boolean(a?.system && a.externalId));
}

export function SettingsCompaniesPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [aliasDraft, setAliasDraft] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const { data: workspace, isLoading: wsLoading } = useQuery({
    queryKey: ["org"],
    queryFn: getOrgWorkspace,
    staleTime: 30_000,
  });

  const org = workspace?.org ?? null;
  const role = workspace?.role ?? null;
  const canManage = role === "owner" || role === "admin";

  const {
    data: portfolioData,
    isLoading: pfLoading,
    error,
  } = useQuery({
    queryKey: ["org-portfolio"],
    queryFn: getOrgPortfolio,
    enabled: Boolean(org),
    staleTime: 15_000,
  });

  const companies = (portfolioData?.companies ?? []).filter(
    (c) => c.status === "active",
  );
  const archived = (portfolioData?.companies ?? []).filter(
    (c) => c.status === "archived",
  );

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["org-portfolio"] });
    void qc.invalidateQueries({ queryKey: ["today"] });
  };

  const createMutation = useMutation({
    mutationFn: () => createOrgCompany({ name: name.trim() }),
    onSuccess: () => {
      setName("");
      setMessage("Company added to portfolio.");
      invalidate();
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, nextName }: { id: string; nextName: string }) =>
      updateOrgCompany(id, { name: nextName }),
    onSuccess: () => {
      setMessage("Company updated.");
      invalidate();
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveOrgCompany(id),
    onSuccess: () => {
      setMessage("Company archived.");
      setEditingId(null);
      invalidate();
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const aliasesMutation = useMutation({
    mutationFn: ({
      id,
      aliases,
    }: {
      id: string;
      aliases: CompanyAlias[];
    }) => setOrgCompanyAliases(id, aliases),
    onSuccess: () => {
      setMessage("Feed aliases saved.");
      setEditingId(null);
      invalidate();
    },
    onError: (err: Error) => setMessage(err.message),
  });

  if (wsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (!org) {
    return (
      <div>
        <SettingsPageHeader
          title="Companies"
          description="Provision the companies in your workspace portfolio."
        />
        <div className="rounded-lg border border-border bg-card p-6">
          <p className="text-sm text-muted-foreground">
            Create a workspace first, then add portfolio companies.
          </p>
          <Link
            to="/settings/workspace-overview"
            className="mt-3 inline-flex text-sm font-medium text-brand hover:underline"
          >
            Set up workspace
          </Link>
        </div>
      </div>
    );
  }

  if (pfLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive">Failed to load portfolio.</p>
      </div>
    );
  }

  function startEdit(company: Company) {
    setEditingId(company.id);
    setAliasDraft(
      (company.aliases ?? [])
        .map((a) => `${a.system}:${a.externalId}`)
        .join("\n"),
    );
  }

  return (
    <div>
      <SettingsPageHeader
        title="Companies"
        description={`${portfolioData?.portfolio.name ?? "Portfolio"} — provision companies for Today filtering and feed mapping.`}
      />

      {message && (
        <p className="mb-4 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
          {message}
        </p>
      )}

      {canManage && (
        <SettingsSection
          title="Add company"
          description="Companies appear in the Today portfolio selector. Feed aliases map Apollo/CSV ids onto these companies."
        >
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!name.trim()) return;
              createMutation.mutate();
            }}
          >
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                Company name
              </span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                placeholder="Acme Payments"
              />
            </label>
            <button
              type="submit"
              disabled={!name.trim() || createMutation.isPending}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              {createMutation.isPending ? "Adding…" : "Add company"}
            </button>
          </form>
        </SettingsSection>
      )}

      <SettingsSection
        title="Active companies"
        description={
          companies.length === 0
            ? "No companies yet. Add one to populate the Today selector."
            : `${companies.length} active`
        }
      >
        <ul className="divide-y divide-border rounded-lg border border-border">
          {companies.length === 0 && (
            <li className="px-4 py-6 text-sm text-muted-foreground">
              Empty portfolio.
            </li>
          )}
          {companies.map((company) => (
            <li key={company.id} className="px-4 py-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {company.name}
                  </p>
                  <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                    {company.id}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Aliases: {formatAliases(company.aliases)}
                  </p>
                </div>
                {canManage && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-border px-2.5 py-1 text-xs font-medium"
                      onClick={() => startEdit(company)}
                    >
                      Edit aliases
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-border px-2.5 py-1 text-xs font-medium"
                      onClick={() => {
                        const next = window.prompt(
                          "Rename company",
                          company.name,
                        );
                        if (next?.trim() && next.trim() !== company.name) {
                          renameMutation.mutate({
                            id: company.id,
                            nextName: next.trim(),
                          });
                        }
                      }}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Archive ${company.name}? It will leave the Today selector.`,
                          )
                        ) {
                          archiveMutation.mutate(company.id);
                        }
                      }}
                    >
                      Archive
                    </button>
                  </div>
                )}
              </div>

              {editingId === company.id && canManage && (
                <div className="mt-3 rounded-md border border-border bg-muted/20 p-3">
                  <label className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-muted-foreground">
                      Feed aliases (one per line: system:externalId)
                    </span>
                    <textarea
                      value={aliasDraft}
                      onChange={(e) => setAliasDraft(e.target.value)}
                      rows={3}
                      className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
                      placeholder={"apollo:12345\ncsv:co-acme"}
                    />
                  </label>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground"
                      disabled={aliasesMutation.isPending}
                      onClick={() =>
                        aliasesMutation.mutate({
                          id: company.id,
                          aliases: parseAliasLines(aliasDraft),
                        })
                      }
                    >
                      Save aliases
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-border px-3 py-1.5 text-xs"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </SettingsSection>

      {archived.length > 0 && (
        <SettingsSection title="Archived" description="Hidden from Today selector.">
          <ul className="space-y-2 text-sm text-muted-foreground">
            {archived.map((c) => (
              <li key={c.id}>
                {c.name}{" "}
                <span className="font-mono text-xs">({c.id})</span>
                {canManage && (
                  <button
                    type="button"
                    className="ml-2 text-xs text-brand hover:underline"
                    onClick={() =>
                      updateOrgCompany(c.id, { status: "active" }).then(() => {
                        setMessage("Company restored.");
                        invalidate();
                      })
                    }
                  >
                    Restore
                  </button>
                )}
              </li>
            ))}
          </ul>
        </SettingsSection>
      )}
    </div>
  );
}
